import React, { useState, useEffect } from 'react';
import { Modal, Typography, Spin, Empty, Table, Tag, Button } from 'antd';
import {
 TeamOutlined,
 SendOutlined,
 CheckOutlined,
 InboxOutlined,
 ClockCircleOutlined,
 QuestionCircleOutlined
} from '@ant-design/icons';
import { FornecedoresStats, Fornecedor } from '../../../../types/dashboard/fornecedores.types';
import FornecedoresService from '../../../../services/dashboard/fornecedores.service';

const { Title, Text } = Typography;

interface CardDetailModalProps {
 visible: boolean;
 onClose: () => void;
 cardType: 'totalFornecedores' | 'questionariosEnviados' | 'pendentes' | 'respondidos' | 'naoRespondidos' | null;
 stats: FornecedoresStats;
 clienteId: string;
}

const CardDetailModal: React.FC<CardDetailModalProps> = ({
 visible,
 onClose,
 cardType,
 stats,
 clienteId
}) => {
 const [loading, setLoading] = useState<boolean>(false);
 const [fornecedores, setFornecedores] = useState<Fornecedor[]>([]);
 const [error, setError] = useState<string | null>(null);
 const [debugMode, setDebugMode] = useState<boolean>(false);

 // Função auxiliar para depuração
 const toggleDebugMode = () => {
   setDebugMode(!debugMode);
 };

 useEffect(() => {
   const fetchDetailData = async () => {
     if (!visible || !cardType || !clienteId) return;

     setLoading(true);
     setError(null);

     try {
       // Dependendo do tipo de card, vamos buscar diferentes dados
       const params = {
         page: 1,
         limit: 1000,
         cliente_id: clienteId
       };

       const result = await FornecedoresService.getFornecedores(params);
       
       if (!result || !result.data || !Array.isArray(result.data)) {
         throw new Error('Formato de resposta inválido');
       }

       let filteredData: Fornecedor[] = [];
       
       const statusUnicos = new Set();
       result.data.forEach(f => {
         if (f.ultimoQuestionario) {
           statusUnicos.add(f.ultimoQuestionario.status);
         } else {
           statusUnicos.add('Sem questionário');
         }
       });

       // Filtrar os fornecedores com base no tipo de card
       switch (cardType) {
         case 'totalFornecedores':
           // Todos os fornecedores
           filteredData = result.data;
           break;
           
         case 'questionariosEnviados':
           // Fornecedores com questionários enviados (incluindo respondidos)
           filteredData = result.data.filter(f => {
             if (!f.ultimoQuestionario) return false;
             
             const status = f.ultimoQuestionario.status || '';
             const statusLower = status.toLowerCase();
             
             // Enviado (excluindo "não enviado")
             const isEnviado = statusLower.includes('enviado') && 
                              !statusLower.includes('não') && 
                              !statusLower.includes('nao');
                              
             // Ou respondido
             const isRespondido = statusLower.includes('respondido');
             
             return isEnviado || isRespondido;
           });
           break;
           
         case 'pendentes':
           // Fornecedores sem questionários ou com status "Não Enviado" ou "Pendente"
           filteredData = result.data.filter(f => {
             // Se não tem ultimoQuestionario, é pendente
             if (!f.ultimoQuestionario) return true;
             
             const status = f.ultimoQuestionario.status || '';
             const statusLower = status.toLowerCase();
             
             // Se o status contém "não enviado", "pendente" ou está vazio
             return statusLower.includes('não enviado') || 
                    statusLower.includes('nao enviado') ||
                    statusLower.includes('pendente') ||  // Adicionando "pendente" como critério
                    statusLower === ''; // Status vazio
           });
           break;
           
         case 'respondidos':
           // Apenas os fornecedores com questionários respondidos
           filteredData = result.data.filter(f => {
             if (!f.ultimoQuestionario) return false;
             
             const status = f.ultimoQuestionario.status || '';
             const statusLower = status.toLowerCase();
             
             // Se contém "respondido"
             return statusLower.includes('respondido');
           });
           break;
           
         case 'naoRespondidos':
           // Fornecedores com questionários enviados mas não respondidos
           filteredData = result.data.filter(f => {
             if (!f.ultimoQuestionario) return false;
             
             const status = f.ultimoQuestionario.status || '';
             const statusLower = status.toLowerCase();
             
             // Verifica se foi enviado
             const isEnviado = statusLower.includes('enviado') && 
                               !statusLower.includes('não') && 
                               !statusLower.includes('nao');
             
             // Verifica se não foi respondido ainda
             const isRespondido = statusLower.includes('respondido');
             
             // Verifica se não está pendente
             const isPendente = statusLower.includes('pendente');
             
             // Retorna verdadeiro para enviados mas não respondidos e não pendentes
             return isEnviado && !isRespondido && !isPendente;
           });
           break;
           
         default:
           filteredData = [];
       }

       setFornecedores(filteredData);
     } catch (error) {
       setError('Falha ao carregar detalhes');
     } finally {
       setLoading(false);
     }
   };

   fetchDetailData();
 }, [visible, cardType, clienteId]);

 // Obter título e ícone com base no tipo do card
 const getCardTitle = () => {
   switch (cardType) {
     case 'totalFornecedores':
       return {
         title: 'Total de Fornecedores',
         icon: <TeamOutlined style={{ marginRight: '8px', fontSize: '24px', color: '#1890ff' }} />,
         value: stats.totalCadastros
       };
     case 'questionariosEnviados':
       return {
         title: 'Questionários Enviados',
         icon: <SendOutlined style={{ marginRight: '8px', fontSize: '24px', color: '#52c41a' }} />,
         value: stats.questionariosEnviados
       };
     case 'pendentes':
       return {
         title: 'Questionários Não Enviados',
         icon: <InboxOutlined style={{ marginRight: '8px', fontSize: '24px', color: '#faad14' }} />,
         value: stats.totalCadastros - stats.questionariosEnviados
       };
     case 'respondidos':
       return {
         title: 'Questionários Respondidos',
         icon: <CheckOutlined style={{ marginRight: '8px', fontSize: '24px', color: '#722ed1' }} />,
         value: stats.questionariosRespondidos
       };
     case 'naoRespondidos':
       return {
         title: 'Questionários Não Respondidos',
         icon: <ClockCircleOutlined style={{ marginRight: '8px', fontSize: '24px', color: '#ff4d4f' }} />,
         value: stats.questionariosEnviados - stats.questionariosRespondidos
       };
     default:
       return {
         title: 'Detalhes',
         icon: null,
         value: 0
       };
   }
 };

 const { title, icon, value } = getCardTitle();

 // Colunas para a tabela de fornecedores
 const columns = [
   {
     title: 'Razão Social',
     dataIndex: 'razaoSocial',
     key: 'razaoSocial',
     render: (text: string) => <Text strong>{text || 'N/A'}</Text>,
     width: 250,
   },
   {
     title: 'CNPJ',
     dataIndex: 'cnpj',
     key: 'cnpj',
     render: (text: string) => text || 'N/A',
     width: 200,
   },
   {
     title: 'Status',
     dataIndex: 'status',
     key: 'status',
     render: (status: string) => {
       let color = 'default';
       const statusLower = (status || '').toLowerCase();
       
       if (statusLower.includes('vigente')) color = 'green';
       else if (statusLower.includes('novo')) color = 'blue';
       else if (statusLower.includes('descontinuado')) color = 'red';
       
       return <Tag color={color}>{status || 'N/A'}</Tag>;
     },
     width: 100,
   },
   {
     title: 'Status Questionário',
     dataIndex: 'ultimoQuestionario',
     key: 'ultimoQuestionario',
     render: (ultimoQuestionario: any) => {
       if (!ultimoQuestionario) {
         return <Tag color="warning">Não Enviado</Tag>;
       }
       
       const status = ultimoQuestionario.status || '';
       const statusLower = status.toLowerCase();
       
       let color = 'default';
       let displayStatus = status;
       
       // Determinar o status e a cor corretos
       if (statusLower.includes('respondido')) {
         color = 'purple';
         displayStatus = 'Respondido';
       } 
       else if (statusLower.includes('enviado') && 
                !statusLower.includes('não') && 
                !statusLower.includes('nao')) {
         
         // Verificar se já está respondido
         if (!statusLower.includes('respondido')) {
           color = 'red';
           displayStatus = 'Não Respondido';
         } else {
           color = 'processing';
           displayStatus = 'Enviado';
         }
       }
       else if (statusLower.includes('não enviado') || 
                statusLower.includes('nao enviado') ||
                statusLower.includes('pendente') ||
                statusLower === '') {
         color = 'warning';
         displayStatus = 'Não Enviado';
       }
       
       return <Tag color={color}>{displayStatus}</Tag>;
     },
     width: 150,
   },
   {
     title: 'Área',
     dataIndex: 'area',
     key: 'area',
     render: (text: string) => text || 'N/A',
     width: 100,
   },
   {
     title: 'Email',
     dataIndex: 'email',
     key: 'email',
     render: (text: string) => text || 'N/A',
     width: 250,
     ellipsis: true
   }
 ];

 return (
   <Modal
     title={
       <div style={{ display: 'flex', alignItems: 'center' }}>
         {icon}
         <span>{title}</span>
         <Text strong style={{ marginLeft: '10px', fontSize: '18px' }}>
           ({value})
         </Text>
       </div>
     }
     open={visible}
     onCancel={onClose}
     footer={null}
     width={1400}
   >
     {loading ? (
       <div style={{ textAlign: 'center', padding: '30px' }}>
         <Spin size="large" />
       </div>
     ) : error ? (
       <div style={{ textAlign: 'center', padding: '20px' }}>
         <Text type="danger">{error}</Text>
       </div>
     ) : fornecedores.length === 0 ? (
       <div style={{ textAlign: 'center', padding: '30px' }}>
         <Empty 
           description={
             <div>
               <Text>Nenhum fornecedor encontrado nesta categoria</Text>
               <br />
               <Text type="secondary" style={{ fontSize: '12px' }}>
                 Verifique se os dados estão corretamente categorizados no sistema.
               </Text>
             </div>
           } 
         />
       </div>
     ) : (
       <div>
         <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
           <Text type="secondary">
             Exibindo {fornecedores.length} fornecedor(es)
           </Text>
           <Button 
             type="text" 
             size="small" 
             icon={<QuestionCircleOutlined />}
             onClick={toggleDebugMode}
           >
             {debugMode ? "Ocultar detalhes" : "Mostrar detalhes"}
           </Button>
         </div>
         
         {/* Modo de depuração para exibir informações sobre os status */}
         {debugMode && (
           <div style={{ marginBottom: '15px', padding: '10px', background: '#f5f5f5', borderRadius: '4px' }}>
             <Text strong>Informações de depuração:</Text>
             <div>
               <Text>Status únicos encontrados: </Text>
               {Array.from(new Set(fornecedores.map(f => 
                 f.ultimoQuestionario ? f.ultimoQuestionario.status : 'Sem questionário'
               ))).map((status, index) => (
                 <Tag key={index} color="blue">{status || 'Vazio'}</Tag>
               ))}
             </div>
           </div>
         )}
         
         <Table
           dataSource={fornecedores}
           columns={columns}
           rowKey="id"
           pagination={{ pageSize: 10 }}
           scroll={{ y: 350, x: 800 }}
         />
       </div>
     )}
   </Modal>
 );
};

export default CardDetailModal;